"use client";

import React from "react";
import { PhoneCall, CalendarCheck, Clock, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const highlights = [{
  id: 1,
  title: "24/7 Immediate Response",
  subtitle: "Plumbers ready around the clock in Jonesboro, GA"
}, {
  id: 2,
  title: "Upfront Transparent Pricing",
  subtitle: "Clear quotes before any repair work begins"
}, {
  id: 3,
  title: "Licensed & Insured",
  subtitle: "Complete protection for residential & commercial jobs"
}];
export default function EmergencyCallout() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="emergencycalltoaction" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative overflow-hidden rounded-2xl bg-primary px-6 py-12 text-primary-foreground shadow-2xl sm:px-12 md:py-16 lg:px-16">
          {/* Subtle decorative background circles */}
          <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 size-80 rounded-full bg-accent/15 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-20 -left-20 size-80 rounded-full bg-primary-foreground/10 blur-3xl" />

          <div className="relative z-10 grid gap-8 lg:grid-cols-12 lg:items-center">
            {/* Main CTA copy */}
            <div className="lg:col-span-7">
              <Badge variant="secondary" className="mb-4 bg-accent text-accent-foreground font-semibold px-3 py-1 text-xs uppercase tracking-wide">
                <Clock className="size-3.5 mr-1" />
                24/7 Urgent Plumbing Assistance
              </Badge>

              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl font-default">
                Plumbing Emergency in Jonesboro? We Are On Our Way.
              </h2>

              <p className="mt-4 max-w-2xl text-base sm:text-lg text-primary-foreground/90 font-default leading-relaxed">
                Burst pipes, severe sewer backups, and broken water heaters need immediate attention. Speak with a master plumber now or send a direct dispatch request.
              </p>

              {/* Highlights grid */}
              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {highlights.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col gap-1 rounded-lg bg-primary-foreground/10 p-3.5 backdrop-blur-xs">
                    <div className="flex items-center gap-1.5 font-semibold text-sm text-accent">
                      <ShieldCheck className="size-4 shrink-0" />
                      <span>{item.title}</span>
                    </div>
                    <span className="text-xs text-primary-foreground/80 leading-snug">
                      {item.subtitle}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Action cards & phone module */}
            <div className="lg:col-span-5 flex flex-col items-stretch gap-4 rounded-xl bg-primary-foreground/10 p-6 sm:p-8 backdrop-blur-sm border border-primary-foreground/20">
              <div className="text-center">
                <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                  Fastest Way To Reach Our Techs
                </span>
                <h3 className="mt-1 text-2xl font-bold tracking-tight text-primary-foreground">
                  Call For Immediate Dispatch
                </h3>
              </div>

              {/* Direct call action */}
              <a href="tel:4708479076" className="group relative flex items-center justify-center gap-3 rounded-xl bg-accent px-6 py-4 text-accent-foreground font-bold text-xl shadow-lg transition-transform active:scale-98 hover:opacity-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring" aria-label="Call (470) 847-9076 immediately">
                <PhoneCall className="size-6 shrink-0 transition-transform group-hover:rotate-12" />
                <span>{"(470) 847-9076"}</span>
              </a>

              <div className="relative flex items-center py-1">
                <div className="grow border-t border-primary-foreground/20" />
                <span className="mx-3 shrink-0 text-xs text-primary-foreground/70 uppercase">
                  or submit online
                </span>
                <div className="grow border-t border-primary-foreground/20" />
              </div>

              {/* Request service button using project Link */}
              <Button asChild size="lg" variant="secondary" className="w-full text-base font-semibold py-6 shadow-sm hover:bg-secondary/90">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <CalendarCheck className="size-5" />
                  <span>Request Service Online</span>
                </Link>
              </Button>

              <p className="text-center text-xs text-primary-foreground/75">
                Typical technician arrival within 45–60 minutes in Clayton County.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}